import React, { useMemo } from 'react';
import Table from '@/components/tables/Table';
import { Client } from '../api/clientsApi';
import { useBlockClient, useUnblockClient } from '../hooks/useClients';
import { Link, useNavigate } from 'react-router-dom';
import { CopyButton } from '@/components/ui/button/CopyButton';
import { useTableColumns } from '@/shared/hooks/useTableColumns';
import { useTranslation } from 'react-i18next';
import Button from '@/components/ui/button/Button';
import Badge from '@/components/ui/badge/Badge';

interface ClientsTableProps {
  clients: Client[];
  isLoading: boolean;
}

function getVerificationBadgeProps(status: string) {
  switch (status) {
    case 'verified':
      return { color: 'success' as const, variant: 'light' as const };
    case 'verification_needed':
      return { color: 'warning' as const, variant: 'light' as const };
    case 'new':
      return { color: 'info' as const, variant: 'light' as const };
    default:
      return { color: 'light' as const, variant: 'light' as const };
  }
}

function formatVerificationStatus(status: string) {
  switch (status) {
    case 'verified':
      return 'Verified';
    case 'verification_needed':
      return 'Verification needed';
    case 'new':
      return 'New';
    default:
      return status;
  }
}

export const ClientsTable: React.FC<ClientsTableProps> = ({ clients, isLoading }) => {
  const { t } = useTranslation();
  const { visibleColumns } = useTableColumns();
  const blockClient = useBlockClient();
  const unblockClient = useUnblockClient();
  const regionName = new Intl.DisplayNames(['en'], { type: 'region' });
  const navigate = useNavigate();

  function getFlagEmoji(countryCode: string) {
    return countryCode
        .toUpperCase()
        .split('')
        .map(char => String.fromCodePoint(0x1F1E6 + char.charCodeAt(0) - 65))
        .join('');
  }

  const columns = [
    {
      key: 'client',
      header: t('clients.columns.client'),
      field: 'client',
      className: 'w-[300px]',
      cell: (row: Client) => (
        <div className="flex flex-col" key={`client-${row.id}`}>
          <span className="font-medium text-text-primary">
            <Link
              to={`/clients/${row.id}`}
              className="hover:underline"
            >
              {row.first_name} {row.last_name}
            </Link>
          </span>
          <span className="text-sm text-text-tertiary flex items-center gap-1">
            <a
              href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(row.email)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {row.email}
            </a>
            <CopyButton text={row.email} title={t('clients.details.copy_email')} />
          </span>
          <span className="text-xs text-text-tertiary">{row.phone}</span>
        </div>
      ),
    },
    {
      key: 'verification',
      header: t('clients.columns.verification'),
      field: 'verification_status',
      cell: (row: Client) => (
        <div className="flex flex-col items-start gap-2">
          <Badge {...getVerificationBadgeProps(row.verification_status)} size="sm">
            {t(`clients.filters.status.${row.verification_status}`)}
          </Badge>
          <div className="flex gap-2 text-xs">
            <span className={row.is_email_confirmed ? 'text-green-600 text-nowrap' : 'text-gray-400 text-nowrap'}>
              {t('clients.details.email')} {row.is_email_confirmed ? '✓' : <span className="text-lg leading-[0.5] relative top-[2.5px]">×</span>}
            </span>
            <span className={row.is_phone_confirmed ? 'text-green-600 text-nowrap' : 'text-gray-400 text-nowrap'}>
              {t('clients.details.phone')} {row.is_phone_confirmed ? '✓' : <span className="text-lg leading-[0.5] relative top-[2.5px]">×</span>}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'registered',
      header: t('clients.columns.registered'),
      field: 'registered_at',
      cell: (row: Client) => (
        <span className="text-sm text-text-primary">
          {new Date(row.registered_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'location',
      header: t('clients.columns.location'),
      field: 'country',
      cell: (row: Client) => (
        <div className="flex flex-col">
          <span className="text-sm flex items-center gap-2 text-text-primary">
            <span className="text-xl">{getFlagEmoji(row.country)}</span>
            <span className="text-sm leading-[1.1]">{regionName.of(row.country)}</span>
          </span>
          {row.city && <span className="text-sm text-text-tertiary">{row.city}</span>}
        </div>
      ),
    },
    {
      key: 'actions',
      header: t('clients.columns.actions'),
      field: 'actions',
      headerClassName: 'text-right',
      className: 'text-right',
      cell: (row: Client) => (
        <div className="flex gap-2 justify-end" key={`actions-${row.id}`}>
          {row.verification_status === 'verification_needed' && (
            <Button
              variant="success"
              size="sm"
              onClick={() => navigate(`/clients/${row.id}#verification-status`)}
            >
              Verify
            </Button>
          )}
          <Link
            to={`/clients/${row.id}`}
            className="px-3 py-1 text-sm text-white bg-brand-500 hover:bg-brand-600 rounded-md"
          >
            {t('clients.details.view_details')}
          </Link>
        </div>
      ),
    },
  ];

  // Фильтруем столбцы на основе visibleColumns из хука
  const filteredColumns = useMemo(() => {
    if (visibleColumns && visibleColumns.length > 0) {
      return columns.filter(col => visibleColumns.includes(col.key));
    }
    return columns;
  }, [columns, visibleColumns]);

  return (
    <>
      <Table<Client>
        data={clients}
        columns={filteredColumns}
        loading={isLoading}
      />
    </>
  );
};
